/**
 * A utility to handle runtime scripts injection, similar to how 3rd party SDKs (fb, gtm, etc.) are loaded
 *
 * ADDING A SCRIPT IN THE LIST:
 * - Add a key to the SCRIPTS constant as the ID of the script
 * - Add the url of script src
 * - [Optional] Add a check function which returns a boolean.
 *              This will be ran to check if script has been injected successuly.
 *              If none is supplied, injection will resolve immediately, regardless if it's still loading.
 * - [Optional] Add a preInjectCheck function which returns a boolean
 *              This will be ran to check if script is ready to be injected. Useful if the script has dependencies.
 *              If none is supplied, this will resolve immediately.
 *
 * INJECTING A SINGLE SCRIPT:
 *    await ScriptInject.inject('script-id')
 * INJECTING MULTIPLE SCRIPTS:
 *    await ScriptInject.inject(['script-id-1', 'script-id-2'])
 *
 */

// Scripts mapped into corresponding IDs
const SCRIPTS = {
	'jquery-ui-core': {
		url: '/wp-includes/js/jquery/ui/core.min.js',
		preInjectCheck: () => (window.jQuery ? true : false),
		check: () => (window.jQuery.ui ? true : false),
	},
	'jquery-ui-menu': {
		url: '/wp-includes/js/jquery/ui/menu.min.js',
		preInjectCheck: () => (window.jQuery?.ui ? true : false),
		check: () => typeof window.jQuery().menu === 'function',
	},
	'jquery-ui-autocomplete': {
		url: `/wp-includes/js/jquery/ui/autocomplete.min.js`,
		check: () => typeof window.jQuery().autocomplete === 'function',
		dependencies: ['jquery-ui-core', 'jquery-ui-menu'],
	},
	highmaps: {
		url: '//code.highcharts.com/maps/highmaps.js',
		check: () => (window.Highcharts ? true : false),
	},
	proj4: {
		url: '//cdnjs.cloudflare.com/ajax/libs/proj4js/2.3.6/proj4.js',
		check: () => typeof window.proj4 === 'function',
	},
	'highmaps-module-data': {
		url: '//code.highcharts.com/maps/modules/data.js',
		check: () => typeof window.Highcharts?.Data === 'function',
	},
	'highcharts-module-export': {
		url: '//code.highcharts.com/modules/export-data.js',
		check: () =>
			'Extensions/Exporting/Exporting.js' in window.Highcharts?._modules,
	},
	'highmaps-module-export': {
		url: '//code.highcharts.com/maps/modules/exporting.js',
		check: () =>
			'Extensions/Exporting/Exporting.js' in window.Highcharts?._modules,
	},
	'highmaps-module-offline-export': {
		url: '//code.highcharts.com/maps/modules/offline-exporting.js',
		preInjectCheck: () =>
			'Core/HttpUtilities.js' in window.Highcharts?._modules,
		check: () =>
			'Extensions/OfflineExporting/OfflineExporting.js' in
			window.Highcharts?._modules,
	},
	'highmaps-mapdata-us': {
		url: '//code.highcharts.com/mapdata/countries/us/us-all.js',
		dependencies: ['highmaps'],
		check: () => 'countries/us/us-all' in window.Highcharts?.maps,
	},
	'highcharts-module-patternfill': {
		url: '//highcharts.github.io/pattern-fill/pattern-fill.js',
	},
	'highcharts-module-accessibility': {
		url: '//code.highcharts.com/modules/accessibility.js',
		check: () =>
			'Accessibility/Accessibility.js' in window.Highcharts?._modules,
	},
};

export type TYPE_SCRIPT_IDS = keyof typeof SCRIPTS;

export interface ScriptDetails {
	url: string;
	check?: () => boolean;
	preInjectCheck?: () => boolean;
	dependencies?: TYPE_SCRIPT_IDS[];
	injected?: boolean;
	requested?: boolean;
	request?: Promise<void>;
}

export type ScriptsMap = {
	[key in TYPE_SCRIPT_IDS]: ScriptDetails;
};

class ScriptInject {
	// Maps script handle to their corresponding URL and checker
	static scripts: ScriptsMap = SCRIPTS as ScriptsMap;

	// Requests injection of a given script handle
	static async inject(scriptId: TYPE_SCRIPT_IDS | TYPE_SCRIPT_IDS[]) {
		const scriptIDs =
			typeof scriptId === 'string'
				? [scriptId]
				: (scriptId as TYPE_SCRIPT_IDS[]);

		await Promise.all(
			scriptIDs.map(async (scriptID) => {
				if (scriptID in this.scripts) {
					if (this.scripts[scriptID].injected) {
						return true;
					} else if (this.scripts[scriptID].requested) {
						await this.scripts[scriptID].request;
						return true;
					}

					await this._requestInjection(scriptID);
					return true;
				}

				throw new Error(
					`Requested script with ID ${scriptID} is not present in the ScriptInject config.`
				);
			})
		).catch((err) => {
			console.error(err);
			throw new Error('Error in injecting script.');
		});
	}

	static async _requestInjection(scriptId: TYPE_SCRIPT_IDS) {
		const scriptConfig = this.scripts[scriptId];

		// Update script flags
		scriptConfig.requested = true;

		// If dependencies are provided, load them first
		if (scriptConfig.dependencies?.length) {
			await this.inject(scriptConfig.dependencies);
		}

		// If preInjectCheck is present, run it before injecting the script
		if (typeof scriptConfig.preInjectCheck === 'function') {
			await new Promise<void>((resolve) => {
				const preInjectCheckInterval = setInterval(() => {
					if (
						scriptConfig.preInjectCheck &&
						scriptConfig.preInjectCheck()
					) {
						clearInterval(preInjectCheckInterval);
						resolve();
					}
				}, 500);
			}).catch((err) => {
				throw err;
			});
		}

		scriptConfig.request = new Promise((resolve, reject) => {
			// If checker is present, periodically run it to resolve injection
			if (typeof scriptConfig.check === 'function') {
				// Set a timeout of 60s when injecting a script
				const timeout = setTimeout(() => {
					clearInterval(checkInterval);
					reject(
						new Error(
							`Script injection check timeout for ${scriptId}`
						)
					);
				}, 60000);

				const checkInterval = setInterval(() => {
					if (scriptConfig.check && scriptConfig.check()) {
						clearInterval(checkInterval);
						clearTimeout(timeout);
						resolve();
					}
				}, 500);
			} else {
				resolve();
			}
		});

		const script = Object.assign(document.createElement('script'), {
			src: scriptConfig.url,
			id: scriptId,
			async: true,
		});

		document.body.appendChild(script);

		await scriptConfig.request;

		scriptConfig.injected = true;

		return true;
	}
}

export default ScriptInject;
